import React, { useState, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import { fetchDashboardData } from "./services/emissionService";

import { doc, getDoc } from "firebase/firestore";
import { firestore } from "./firebase.config";

import "./CbamDashboard.css";
import AuditSidebar from "./AuditSidebar";
import { useParams } from "react-router-dom";
import useAuditProgress from "../hooks/useAuditProgress";

const CbamDashboard = () => {
  const { auditId } = useParams();

  const { formsData } = useAuditProgress(auditId);

  const [metrics, setMetrics] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [meta, setMeta] = useState(null);

  const [currencySettings, setCurrencySettings] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const dashboardData = await fetchDashboardData(auditId);

        setMeta(dashboardData.meta);
        setMetrics(dashboardData.overview.metrics);

        const settingsRef = doc(
          firestore,
          "auditReports",
          auditId,
          "settings",
          "currency",
        );

        const settingsSnap = await getDoc(settingsRef);

        if (settingsSnap.exists()) {
          setCurrencySettings(settingsSnap.data());
        } else {
          setCurrencySettings({
            baseCarbonPrice: 76,
            usdRate: 1.17,
            gbpRate: 0.86,
            inrRate: 102.4,
          });
        }
      } catch (error) {
        console.error("Data load failed", error);

        setCurrencySettings({
          baseCarbonPrice: 76,
          usdRate: 1.17,
          gbpRate: 0.86,
          inrRate: 102.4,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [auditId]);

  if (isLoading || !metrics || !meta || !currencySettings) {
    return <div className="loading">Loading CBAM Data...</div>;
  }

  // --- CALCULATION LOGIC ---
  const euroValue = metrics.totalEmissions * currencySettings.baseCarbonPrice;

  const usdValue = euroValue * currencySettings.usdRate;

  const gbpValue = euroValue * currencySettings.gbpRate;

  const inrValue = euroValue * currencySettings.inrRate;

  const getPieChartOptions = () => {
    return {
      tooltip: { trigger: "item", formatter: "{b}: {c} tCO₂e ({d}%)" },
      legend: {
        bottom: "2%",
        left: "center",
        orient: "horizontal",
        icon: "circle",

        itemGap: 45,
        itemWidth: 12,
        itemHeight: 12,

        textStyle: {
          color: "#374151",
          fontSize: 14,
          fontWeight: "600",
        },
      },
      // Using the exact Blue and Green colors from your image
      color: [
        "#2563EB", // Direct
        "#93C5FD", // Indirect
      ],
      series: [
        {
          name: "Emissions",
          type: "pie",
          radius: ["35%", "85%"],
          center: ["50%", "45%"],
          avoidLabelOverlap: false,
          itemStyle: {
            borderRadius: 4,
            borderColor: "#ffffff",
            borderWidth: 2,
          },
          label: {
            show: true,
            position: "inner",
            color: "#ffffff",
            fontWeight: "bold",
            fontSize: 18,
            formatter: (params) => params.value.toLocaleString(),
          },
          data: [
            { value: metrics.directEmissions, name: "Direct" },
            { value: metrics.indirectEmissions, name: "Indirect" },
          ],
        },
      ],
    };
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", width: "100%" }}>
      <AuditSidebar displayAuditId={auditId} formsData={formsData} />

      <div style={{ flex: 1, overflow: "auto" }}>
        <div className="dashboard-container">
          <header className="dashboard-header">
            <h1>Dashboard</h1>
          </header>

          {/* ---------- TOP KPI CARDS ---------- */}

          <div className="kpi-grid-top">
            {/* Direct */}
            <div className="kpi-card">
              <div className="kpi-header">
                <span className="kpi-label">Total Direct Emissions</span>

                <span className="unit-text">(in tCO₂e)</span>
              </div>
              <p className="kpi-value blue">
                {metrics.directEmissions.toLocaleString()}
              </p>
            </div>

            {/* Indirect */}
            <div className="kpi-card ">
              <div className="kpi-header">
                <span className="kpi-label">Total Indirect Emissions</span>

                <span className="unit-text">(in tCO₂e)</span>
              </div>
              <p className="kpi-value blue">
                {metrics.indirectEmissions.toLocaleString()}
              </p>
            </div>

            {/* Total Emissions */}
            <div className="kpi-card">
              <div className="kpi-header">
                <span className="kpi-label">Total Emissions</span>

                <span className="unit-text">(in tCO₂e)</span>
              </div>
              <p className="kpi-value blue">
                {metrics.totalEmissions.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="analytics-grid">
            {/* Emission Breakdown */}

            <div className="analytics-card">
              <div className="card-header-flex">
                <p className="chart-title">Emissions Breakdown</p>
              </div>

              <ReactECharts
                option={getPieChartOptions()}
                style={{
                  height: "320px",
                  width: "100%",
                }}
              />
            </div>

            {/* CBAM */}
            <div className="analytics-card cbam-card">
              <div className="card-header-flex">
                <h2 className="cbam-title">Total CBAM Cost Exposure</h2>
              </div>

              <p className="cbam-label">
                Base Carbon Price: {currencySettings.baseCarbonPrice} €/tCO₂e
              </p>

              <div className="cbam-total">
                <div className="euro-section">
                  <span className="euro-symbol">€</span>

                  <span className="euro-value">
                    {euroValue.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </div>

                <div className="currency-list">
                  <div className="currency-row usd-row">
                    <span>USD</span>
                    <span>
                      $
                      {usdValue.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>

                  <div className="currency-row gbp-row">
                    <span>GBP</span>
                    <span>
                      £
                      {gbpValue.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>

                  <div className="currency-row inr-row">
                    <span>INR</span>
                    <span>
                      ₹
                      {inrValue.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CbamDashboard;
